/** @format */

import type { TraceAnalysisDetailSpan } from '../typescript'
import { getLangGraphNode, LANGGRAPH_ROOT_KEY, sortSpanSiblings } from './langgraphSpanTree'

export interface SpanTreeNode {
  span: TraceAnalysisDetailSpan
  children: SpanTreeNode[]
  x?: number
  y?: number
}

export type SpanTableRow = TraceAnalysisDetailSpan & {
  children?: SpanTableRow[]
}

export interface SpanFilterOptions {
  searchValue?: string
  selectedTags?: string[]
  showErrorSpans?: boolean
}

export function spanMatchesKind(span: TraceAnalysisDetailSpan, selectedTags: string[]): boolean {
  if (!selectedTags.length) return true
  return selectedTags.includes(span.Kind)
}

export function spanMatchesName(span: TraceAnalysisDetailSpan, keyword: string): boolean {
  const kw = keyword.trim().toLowerCase()
  if (!kw) return true
  return (span.Name || '').toLowerCase().includes(kw)
}

/** `showErrorSpans === true` 时仅保留非 OK Span */
export function spanMatchesError(span: TraceAnalysisDetailSpan, showErrorSpans: boolean): boolean {
  if (!showErrorSpans) return true
  return span.StatusCode === 'ERROR'
}

export function spanMatchesFilters(
  span: TraceAnalysisDetailSpan,
  keyword: string,
  selectedTags: string[],
  showErrorSpans: boolean
): boolean {
  return (
    spanMatchesKind(span, selectedTags) &&
    spanMatchesName(span, keyword) &&
    spanMatchesError(span, showErrorSpans)
  )
}

export function isSpanFilterActive(
  keyword: string,
  selectedTags: string[],
  showErrorSpans: boolean
): boolean {
  return selectedTags.length > 0 || keyword.trim().length > 0 || showErrorSpans
}

export function getMatchedSpanIds(
  spans: TraceAnalysisDetailSpan[],
  { searchValue = '', selectedTags = [], showErrorSpans = false }: SpanFilterOptions
): Set<string> {
  if (!isSpanFilterActive(searchValue, selectedTags, showErrorSpans)) {
    return new Set()
  }
  return new Set(
    spans
      .filter(span => spanMatchesFilters(span, searchValue, selectedTags, showErrorSpans))
      .map(span => span.SpanId)
  )
}

export function buildSpanTree(
  map: Map<string, TraceAnalysisDetailSpan[]>,
  parentKey: string,
  langGraphMode: boolean
): SpanTreeNode[] {
  return sortSpanSiblings(map.get(parentKey) ?? [], langGraphMode).map(span => ({
    span,
    children: buildSpanTree(map, span.SpanId, langGraphMode)
  }))
}

export function toTableTreeData(nodes: SpanTreeNode[]): SpanTableRow[] {
  return nodes.map(node => ({
    ...node.span,
    children: node.children.length ? toTableTreeData(node.children) : undefined
  }))
}

export function collectExpandableSpanIds(nodes: SpanTreeNode[]): string[] {
  const ids: string[] = []
  const walk = (list: SpanTreeNode[]) => {
    for (const node of list) {
      if (node.children.length > 0) {
        ids.push(node.span.SpanId)
        walk(node.children)
      }
    }
  }
  walk(nodes)
  return ids
}

export function walkSpanTree(nodes: SpanTreeNode[], visit: (node: SpanTreeNode) => void) {
  for (const node of nodes) {
    visit(node)
    if (node.children.length) {
      walkSpanTree(node.children, visit)
    }
  }
}

/** ERROR 为失败；存在 exception 事件视为告警 */
export function spanStatusVisual(span: TraceAnalysisDetailSpan): 'ok' | 'warn' | 'err' {
  if (span.StatusCode === 'ERROR') return 'err'
  if (span.Events?.some(event => event.Name === 'exception')) return 'warn'
  return 'ok'
}

export function spanNodeLabel(span: TraceAnalysisDetailSpan): string {
  const graphNode = getLangGraphNode(span.Attributes)
  const name = (span.Name || '').trim()
  if (graphNode && name === graphNode) return graphNode
  return name || graphNode || span.SpanId || '-'
}

export { LANGGRAPH_ROOT_KEY }
